import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FaBoxOpen,
  FaCalendarCheck,
  FaCalendarTimes,
  FaChartLine,
  FaCheckCircle,
  FaClipboardList,
  FaCogs,
  FaDownload,
  FaEdit,
  FaExclamationTriangle,
  FaFileImport,
  FaPaperclip,
  FaMapMarkedAlt,
  FaPlus,
  FaTools,
  FaTrash,
  FaUserShield,
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import Chart from 'chart.js/auto';
import { read as readXLSX, utils as XLSXUtils, writeFile as writeXLSXFile } from 'xlsx';
import AssetService from '../services/AssetService';
import Modal from '../components/Modal';
import { useAuth } from '../AuthContext';
import {
  ASSET_MANAGE_PERMISSION,
  ASSET_VIEW_PERMISSION,
  hasPermission,
} from '../utils/permissions';

const STATUS_OPTIONS = [
  { value: 'OPERATING', label: 'Em operação' },
  { value: 'MAINTENANCE', label: 'Em manutenção' },
  { value: 'STOCK', label: 'Em estoque' },
  { value: 'DISPOSED', label: 'Baixado' },
];

const IMPORT_REQUIRED_FIELDS = ['name', 'status', 'category', 'location'];

const IMPORT_COLUMNS = [
  { key: 'name', label: 'Nome do ativo', required: true, instructions: 'Nome principal do ativo.' },
  { key: 'tag', label: 'Etiqueta', required: false, instructions: 'Código interno/patrimônio.' },
  { key: 'serialNumber', label: 'Número de série', required: false, instructions: 'Número de série do fabricante.' },
  { key: 'status', label: 'Status', required: true, instructions: 'Em operação, Em manutenção, Em estoque ou Baixado.' },
  { key: 'category', label: 'Categoria', required: true, instructions: 'Nome da categoria já cadastrada.' },
  { key: 'location', label: 'Localização', required: true, instructions: 'Nome do local já cadastrado.' },
  { key: 'acquisitionDate', label: 'Data de aquisição', required: false, instructions: 'Data no formato AAAA-MM-DD.' },
  { key: 'acquisitionCost', label: 'Valor de aquisição', required: false, instructions: 'Valor numérico (ex: 14500.90).' },
  { key: 'depreciationRate', label: 'Taxa de depreciação (%)', required: false, instructions: 'Percentual anual.' },
  { key: 'usefulLifeMonths', label: 'Vida útil (meses)', required: false, instructions: 'Quantidade de meses.' },
  { key: 'warrantyEnd', label: 'Garantia até', required: false, instructions: 'Data no formato AAAA-MM-DD.' },
  { key: 'custodian', label: 'Responsável', required: false, instructions: 'Pessoa/área responsável.' },
  { key: 'supplier', label: 'Fornecedor', required: false, instructions: 'Nome do fornecedor.' },
  { key: 'notes', label: 'Observações', required: false, instructions: 'Detalhes complementares.' },
];

const getImportColumnLabel = (column) => `${column.required ? '* ' : ''}${column.label}`;

const MAINTENANCE_TYPE_OPTIONS = [
  { value: 'PREVENTIVE', label: 'Preventiva' },
  { value: 'CORRECTIVE', label: 'Corretiva' },
  { value: 'PREDICTIVE', label: 'Preditiva' },
  { value: 'CALIBRATION', label: 'Calibração' },
];

const MAINTENANCE_STATUS_OPTIONS = [
  { value: 'SCHEDULED', label: 'Agendada' },
  { value: 'IN_PROGRESS', label: 'Em execução' },
  { value: 'COMPLETED', label: 'Concluída' },
  { value: 'CANCELLED', label: 'Cancelada' },
];

const MAINTENANCE_PERIODICITY_UNIT_OPTIONS = [
  { value: 'DAYS', label: 'Dias' },
  { value: 'WEEKS', label: 'Semanas' },
  { value: 'MONTHS', label: 'Meses' },
  { value: 'YEARS', label: 'Anos' },
];

const maintenanceTypeLabels = MAINTENANCE_TYPE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const maintenanceStatusLabels = MAINTENANCE_STATUS_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const emptyAssetForm = {
  id: null,
  name: '',
  serialNumber: '',
  tag: '',
  status: 'OPERATING',
  categoryId: '',
  locationId: '',
  acquisitionDate: '',
  acquisitionCost: '',
  depreciationRate: '15',
  usefulLifeMonths: '',
  warrantyEnd: '',
  custodian: '',
  supplier: '',
  notes: '',
  attachments: [],
};

const emptyMaintenanceForm = {
  id: null,
  assetId: null,
  type: 'PREVENTIVE',
  status: 'SCHEDULED',
  periodicityValue: '',
  periodicityUnit: 'DAYS',
  scheduledAt: '',
  performedAt: '',
  responsible: '',
  cost: '',
  notes: '',
};

const emptyCategoryForm = {
  id: null,
  name: '',
  description: '',
  color: '#0ea5e9',
};

const emptyLocationForm = {
  id: null,
  name: '',
  description: '',
  responsible: '',
  contact: '',
};

const resolveErrorMessage = (error) => {
  if (!error) return 'Ocorreu um erro inesperado.';
  if (error.response?.data?.message) return error.response.data.message;
  if (error.message) return error.message;
  return 'Não foi possível concluir a operação.';
};

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(numeric);
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR');
};

const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const toInputDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const [day, time] = date.toISOString().split('T');
  return `${day}T${time.slice(0, 5)}`;
};

const differenceInMonths = (start, end) => {
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return 0;
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return 0;
  const startYear = start.getFullYear();
  const startMonth = start.getMonth();
  const endYear = end.getFullYear();
  const endMonth = end.getMonth();
  let months = (endYear - startYear) * 12 + (endMonth - startMonth);
  if (end.getDate() < start.getDate()) {
    months -= 1;
  }
  return Math.max(0, months);
};

const daysUntil = (value) => {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const diff = target.getTime() - Date.now();
  return Math.round(diff / (1000 * 60 * 60 * 24));
};

const formatPercentage = (value) => {
  if (!Number.isFinite(Number(value))) return '0%';
  return `${Math.round(Number(value))}%`;
};

const resolveTextValue = (value) => {
  if (value === null || typeof value === 'undefined') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => resolveTextValue(item)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const preferredKeys = ['name', 'label', 'title', 'description', 'value'];
    for (const key of preferredKeys) {
      const resolved = resolveTextValue(value[key]);
      if (resolved) return resolved;
    }
  }
  return '';
};

const formatFileSize = (size) => {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let current = bytes / 1024;
  let unitIndex = 0;
  while (current >= 1024 && unitIndex < units.length - 1) {
    current /= 1024;
    unitIndex += 1;
  }
  return `${current.toFixed(current >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const normalizeHeader = (value) =>
  normalizeText(value)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const parseImportNumber = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes(',') && raw.includes('.')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw.replace(',', '.');
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
};

const loadImageAsDataURL = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Erro ao carregar a imagem.'));
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Erro ao carregar imagem do relatório:', error);
    return null;
  }
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Não foi possível processar o anexo.'));
    reader.readAsDataURL(file);
  });

const dataUrlToBlob = (dataUrl) => {
  if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) return null;
  const separatorIndex = dataUrl.indexOf(',');
  if (separatorIndex < 0) return null;
  const metadata = dataUrl.slice(5, separatorIndex);
  const content = dataUrl.slice(separatorIndex + 1);
  const isBase64 = metadata.includes(';base64');
  const mimeType = metadata.split(';')[0] || 'application/octet-stream';

  try {
    if (isBase64) {
      const binary = window.atob(content);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }
      return new Blob([bytes], { type: mimeType });
    }
    return new Blob([decodeURIComponent(content)], { type: mimeType });
  } catch (error) {
    console.error('Erro ao converter anexo em blob:', error);
    return null;
  }
};

const createChartImage = async (config) => {
  if (typeof window === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 400;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const providedOptions = config.options || {};
  const providedPlugins = providedOptions.plugins || {};

  const legendLabels = {
    font: {
      family: 'Helvetica',
      size: 14,
      ...(providedPlugins.legend?.labels?.font || {}),
    },
    color: '#0f172a',
    ...(providedPlugins.legend?.labels || {}),
  };

  const legendConfig = {
    ...providedPlugins.legend,
    labels: legendLabels,
  };

  const titleConfig = {
    color: '#0f172a',
    font: {
      family: 'Helvetica',
      size: 18,
      ...(providedPlugins.title?.font || {}),
    },
    padding: {
      top: 12,
      bottom: 12,
      ...(providedPlugins.title?.padding || {}),
    },
    ...providedPlugins.title,
  };

  if (typeof titleConfig.display === 'undefined') {
    titleConfig.display = Boolean(titleConfig.text);
  }

  const chart = new Chart(context, {
    ...config,
    options: {
      responsive: false,
      animation: false,
      ...providedOptions,
      plugins: {
        ...providedPlugins,
        legend: legendConfig,
        title: titleConfig,
      },
    },
  });

  await new Promise((resolve) => setTimeout(resolve, 150));
  const image = chart.toBase64Image();
  chart.destroy();
  return image;
};

const computeAssetMetrics = (asset) => {
  const acquisitionCost = Number(asset.acquisitionCost || 0);
  const depreciationRate = Number(asset.depreciationRate || 0);
  const acquisitionDate = asset.acquisitionDate ? new Date(asset.acquisitionDate) : null;
  const now = new Date();
  const monthsInUse = acquisitionDate ? differenceInMonths(acquisitionDate, now) : 0;
  const monthlyRate = (acquisitionCost * depreciationRate) / (12 * 100);
  const accumulatedDepreciation = Math.min(
    acquisitionCost,
    Math.max(0, monthsInUse) * Math.max(0, monthlyRate),
  );
  const currentValue = Math.max(0, acquisitionCost - accumulatedDepreciation);
  const depreciationPercentage = acquisitionCost
    ? Math.min(100, (accumulatedDepreciation / acquisitionCost) * 100)
    : 0;

  const maintenance = Array.isArray(asset.maintenance) ? asset.maintenance : [];
  const openMaintenance = maintenance.filter((entry) => entry.status !== 'COMPLETED');
  const upcomingMaintenance = openMaintenance
    .filter((entry) => entry.scheduledAt)
    .slice()
    .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))[0] || null;

  return {
    acquisitionCost,
    monthsInUse,
    accumulatedDepreciation,
    currentValue,
    depreciationPercentage,
    openMaintenanceCount: openMaintenance.length,
    upcomingMaintenance,
  };
};

const statusBadgeStyles = {
  OPERATING: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  MAINTENANCE: 'bg-amber-100 text-amber-800 border-amber-200',
  STOCK: 'bg-slate-100 text-slate-700 border-slate-200',
  DISPOSED: 'bg-rose-100 text-rose-700 border-rose-200',
};

const statusLabels = {
  OPERATING: 'Em operação',
  MAINTENANCE: 'Em manutenção',
  STOCK: 'Em estoque',
  DISPOSED: 'Baixado',
};

const chartPalette = [
  '#2563eb',
  '#0ea5e9',
  '#22c55e',
  '#f97316',
  '#f43f5e',
  '#8b5cf6',
  '#14b8a6',
  '#f59e0b',
  '#475569',
];

const TAB_ITEMS = [
  { id: 'overview', label: 'Visão geral', icon: FaChartLine },
  { id: 'assets', label: 'Ativos', icon: FaClipboardList },
  { id: 'import', label: 'Importação', icon: FaFileImport },
  { id: 'maintenance', label: 'Manutenções', icon: FaTools },
  { id: 'directories', label: 'Cadastros', icon: FaCogs },
];

export default function AssetManagement() {
  const { user } = useAuth();
  const canManage = hasPermission(user, ASSET_MANAGE_PERMISSION);
  const canView = canManage || hasPermission(user, ASSET_VIEW_PERMISSION);

  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [importPreviewRows, setImportPreviewRows] = useState([]);
  const [importingAssets, setImportingAssets] = useState(false);

  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('success');
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    categoryId: 'all',
    locationId: 'all',
  });

  const [activeTab, setActiveTab] = useState('overview');

  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetForm, setAssetForm] = useState(emptyAssetForm);
  const [savingAsset, setSavingAsset] = useState(false);

  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState(emptyMaintenanceForm);
  const [savingMaintenance, setSavingMaintenance] = useState(false);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [savingCategory, setSavingCategory] = useState(false);

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locationForm, setLocationForm] = useState(emptyLocationForm);
  const [savingLocation, setSavingLocation] = useState(false);

  const showFeedback = useCallback((message, type = 'success') => {
    setFeedback(message);
    setFeedbackType(type);
    if (message) {
      setTimeout(() => setFeedback(''), 5000);
    }
  }, []);

  const handleError = useCallback((message) => {
    setError(message);
    if (message) {
      setTimeout(() => setError(''), 7000);
    }
  }, []);

  const loadAssets = useCallback(async () => {
    try {
      const response = await AssetService.listAssets();
      const data = Array.isArray(response.data) ? response.data : [];
      setAssets(data);
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setAssets([]);
    }
  }, [handleError]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await AssetService.listCategories();
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setCategories([]);
    }
  }, [handleError]);

  const loadLocations = useCallback(async () => {
    try {
      const response = await AssetService.listLocations();
      setLocations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setLocations([]);
    }
  }, [handleError]);

  const loadAll = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    await Promise.allSettled([loadAssets(), loadCategories(), loadLocations()]);
    setLoading(false);
  }, [canView, loadAssets, loadCategories, loadLocations]);

  useEffect(() => {
    if (!canView) return;
    loadAll();
  }, [canView, loadAll]);

  const assetsWithMetrics = useMemo(
    () => assets.map((asset) => ({ ...asset, ...computeAssetMetrics(asset) })),
    [assets],
  );

  const filteredAssets = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return assetsWithMetrics.filter((asset) => {
      const matchesTerm = term
        ? [
            asset.name,
            asset.serialNumber,
            asset.tag,
            asset.custodian,
            asset.category?.name,
            asset.location?.name,
          ]
            .filter(Boolean)
            .some((candidate) => candidate.toLowerCase().includes(term))
        : true;

      const matchesStatus =
        filters.status === 'all' || asset.status === filters.status;

      const matchesCategory =
        filters.categoryId === 'all' || asset.category?.id === filters.categoryId;

      const matchesLocation =
        filters.locationId === 'all' || asset.location?.id === filters.locationId;

      return matchesTerm && matchesStatus && matchesCategory && matchesLocation;
    });
  }, [assetsWithMetrics, filters]);

  const filteredTotals = useMemo(() => {
    const totalAcquisition = filteredAssets.reduce(
      (total, asset) => total + Number(asset.acquisitionCost || 0),
      0,
    );
    const totalCurrent = filteredAssets.reduce(
      (total, asset) => total + Number(asset.currentValue || 0),
      0,
    );
    const averageDepreciation = filteredAssets.length
      ? filteredAssets.reduce(
          (total, asset) => total + Number(asset.depreciationPercentage || 0),
          0,
        ) / filteredAssets.length
      : 0;

    return {
      totalAcquisition,
      totalCurrent,
      averageDepreciation,
      assetCount: filteredAssets.length,
    };
  }, [filteredAssets]);

  const statusDistributionSummary = useMemo(() => {
    if (!filteredAssets.length) {
      return { labels: [], values: [] };
    }
    const counts = filteredAssets.reduce((acc, asset) => {
      const key = asset.status || 'UNKNOWN';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const labels = Object.keys(counts).map((status) => statusLabels[status] || status);
    const values = Object.values(counts);
    return { labels, values };
  }, [filteredAssets]);

  const categoryDistributionSummary = useMemo(() => {
    if (!filteredAssets.length) {
      return { labels: [], values: [] };
    }
    const counts = filteredAssets.reduce((acc, asset) => {
      const key = asset.category?.name || 'Sem categoria';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const labels = Object.keys(counts);
    const values = Object.values(counts);
    return { labels, values };
  }, [filteredAssets]);

  const warrantySummary = useMemo(() => {
    const expiring = [];
    const expired = [];
    assetsWithMetrics.forEach((asset) => {
      const days = daysUntil(asset.warrantyEnd);
      if (days === null) return;
      if (days < 0) {
        expired.push(asset);
      } else if (days <= 45) {
        expiring.push({ ...asset, days });
      }
    });
    expiring.sort((a, b) => a.days - b.days);
    return { expiring, expired };
  }, [assetsWithMetrics]);

  const upcomingMaintenance = useMemo(() => {
    const entries = [];
    assetsWithMetrics.forEach((asset) => {
      (asset.maintenance || []).forEach((entry) => {
        entries.push({ asset, entry });
      });
    });
    return entries
      .filter(({ entry }) => entry.status !== 'COMPLETED')
      .sort((a, b) => {
        const dateA = new Date(a.entry.scheduledAt || a.entry.performedAt || 0).getTime();
        const dateB = new Date(b.entry.scheduledAt || b.entry.performedAt || 0).getTime();
        return dateA - dateB;
      });
  }, [assetsWithMetrics]);

  const maintenanceHistory = useMemo(() => {
    const entries = [];
    assetsWithMetrics.forEach((asset) => {
      (asset.maintenance || []).forEach((entry) => {
        entries.push({ asset, entry });
      });
    });
    return entries
      .slice()
      .sort((a, b) => {
        const dateA = new Date(a.entry.performedAt || a.entry.scheduledAt || 0).getTime();
        const dateB = new Date(b.entry.performedAt || b.entry.scheduledAt || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, 12);
  }, [assetsWithMetrics]);

  const totalAcquisitionValue = useMemo(
    () =>
      assetsWithMetrics.reduce(
        (total, asset) => total + Number(asset.acquisitionCost || 0),
        0,
      ),
    [assetsWithMetrics],
  );

  const totalCurrentValue = useMemo(
    () => assetsWithMetrics.reduce((total, asset) => total + asset.currentValue, 0),
    [assetsWithMetrics],
  );

  const pendingMaintenanceCount = useMemo(
    () =>
      assetsWithMetrics.reduce(
        (total, asset) => total + Number(asset.openMaintenanceCount || 0),
        0,
      ),
    [assetsWithMetrics],
  );

  const highlyDepreciatedAssets = useMemo(
    () => assetsWithMetrics.filter((asset) => asset.depreciationPercentage >= 70).length,
    [assetsWithMetrics],
  );

  const topDepreciatedAssets = useMemo(() => {
    const candidates = assetsWithMetrics.filter((asset) =>
      Number.isFinite(Number(asset.depreciationPercentage)),
    );
    return candidates
      .slice()
      .sort(
        (a, b) =>
          Number(b.depreciationPercentage || 0) - Number(a.depreciationPercentage || 0),
      )
      .slice(0, 5);
  }, [assetsWithMetrics]);

  const openAssetModal = useCallback(
    (asset = null) => {
      if (asset) {
        setAssetForm({
          id: asset.id,
          name: asset.name || '',
          serialNumber: asset.serialNumber || '',
          tag: asset.tag || '',
          status: asset.status || 'OPERATING',
          categoryId: asset.category?.id || '',
          locationId: asset.location?.id || '',
          acquisitionDate: toInputDate(asset.acquisitionDate),
          acquisitionCost: asset.acquisitionCost ? String(asset.acquisitionCost) : '',
          depreciationRate: asset.depreciationRate ? String(asset.depreciationRate) : '',
          usefulLifeMonths: asset.usefulLifeMonths ? String(asset.usefulLifeMonths) : '',
          warrantyEnd: toInputDate(asset.warrantyEnd),
          custodian: asset.custodian || '',
          supplier: asset.supplier || '',
          notes: asset.notes || '',
          attachments: Array.isArray(asset.attachments) ? asset.attachments : [],
        });
      } else {
        setAssetForm(emptyAssetForm);
      }
      setAssetModalOpen(true);
    },
    [],
  );

  const openMaintenanceModal = useCallback((asset) => {
    if (!asset) return;
    setMaintenanceForm({
      ...emptyMaintenanceForm,
      assetId: asset.id,
      responsible: asset.custodian || '',
    });
    setMaintenanceModalOpen(true);
  }, []);

  const openCategoryModal = useCallback((category = null) => {
    if (category) {
      setCategoryForm({
        id: category.id,
        name: category.name || '',
        description: category.description || '',
        color: category.color || '#0ea5e9',
      });
    } else {
      setCategoryForm(emptyCategoryForm);
    }
    setCategoryModalOpen(true);
  }, []);

  const openLocationModal = useCallback((location = null) => {
    if (location) {
      setLocationForm({
        id: location.id,
        name: location.name || '',
        description: location.description || '',
        responsible: location.responsible || '',
        contact: location.contact || '',
      });
    } else {
      setLocationForm(emptyLocationForm);
    }
    setLocationModalOpen(true);
  }, []);

  const handleSaveAsset = useCallback(async () => {
    setSavingAsset(true);
    try {
      const payload = {
        ...assetForm,
        acquisitionCost: assetForm.acquisitionCost
          ? Number(assetForm.acquisitionCost)
          : 0,
        depreciationRate: assetForm.depreciationRate
          ? Number(assetForm.depreciationRate)
          : 0,
        usefulLifeMonths: assetForm.usefulLifeMonths
          ? Number(assetForm.usefulLifeMonths)
          : null,
        attachments: Array.isArray(assetForm.attachments) ? assetForm.attachments : [],
      };
      if (assetForm.id) {
        await AssetService.updateAsset(assetForm.id, payload);
        showFeedback('Ativo atualizado com sucesso.');
      } else {
        await AssetService.createAsset(payload);
        showFeedback('Ativo cadastrado com sucesso.');
      }
      setAssetModalOpen(false);
      await loadAssets();
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setSavingAsset(false);
    }
  }, [assetForm, loadAssets, showFeedback, handleError]);

  const handleAddAssetAttachments = useCallback(
    async (files) => {
      if (!files || files.length === 0) return;
      try {
        const nextAttachments = await Promise.all(
          Array.from(files).map(async (file) => ({
            id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
            name: file.name || 'anexo',
            url: await readFileAsDataUrl(file),
            type: file.type || 'application/octet-stream',
            size: Number(file.size) || 0,
            uploadedAt: new Date().toISOString(),
          })),
        );
        setAssetForm((prev) => ({
          ...prev,
          attachments: [...(Array.isArray(prev.attachments) ? prev.attachments : []), ...nextAttachments],
        }));
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [handleError],
  );

  const handleRemoveAssetAttachment = useCallback((attachmentId) => {
    setAssetForm((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((attachment) => attachment.id !== attachmentId),
    }));
  }, []);

  const handleOpenAssetAttachment = useCallback(
    (attachment) => {
      if (!attachment?.url) {
        handleError('Anexo inválido para visualização.');
        return;
      }

      if (!attachment.url.startsWith('data:')) {
        window.open(attachment.url, '_blank', 'noopener,noreferrer');
        return;
      }

      const blob = dataUrlToBlob(attachment.url);
      if (!blob) {
        handleError('Não foi possível abrir o anexo selecionado.');
        return;
      }
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    },
    [handleError],
  );

  const handleDeleteAsset = useCallback(
    async (asset) => {
      if (!asset || !window.confirm(`Excluir o ativo "${asset.name}"?`)) return;
      try {
        await AssetService.deleteAsset(asset.id);
        showFeedback('Ativo excluído com sucesso.');
        await loadAssets();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [loadAssets, showFeedback, handleError],
  );

  const handleSaveMaintenance = useCallback(async () => {
    if (!maintenanceForm.assetId) return;
    setSavingMaintenance(true);
    try {
      const payload = {
        type: maintenanceForm.type,
        status: maintenanceForm.status,
        periodicityValue: maintenanceForm.periodicityValue
          ? Number(maintenanceForm.periodicityValue)
          : null,
        periodicityUnit: maintenanceForm.periodicityUnit,
        scheduledAt: maintenanceForm.scheduledAt,
        performedAt: maintenanceForm.performedAt,
        responsible: maintenanceForm.responsible,
        cost: maintenanceForm.cost ? Number(maintenanceForm.cost) : 0,
        notes: maintenanceForm.notes,
      };
      if (maintenanceForm.id) {
        await AssetService.updateMaintenance(
          maintenanceForm.assetId,
          maintenanceForm.id,
          payload,
        );
        showFeedback('Manutenção atualizada com sucesso.');
      } else {
        await AssetService.recordMaintenance(maintenanceForm.assetId, payload);
        showFeedback('Manutenção registrada com sucesso.');
      }
      setMaintenanceModalOpen(false);
      await loadAssets();
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setSavingMaintenance(false);
    }
  }, [maintenanceForm, loadAssets, showFeedback, handleError]);

  const handleCompleteMaintenance = useCallback(
    async (assetId, maintenance) => {
      try {
        await AssetService.updateMaintenance(assetId, maintenance.id, {
          status: 'COMPLETED',
          performedAt: new Date().toISOString(),
        });
        showFeedback('Manutenção concluída.');
        await loadAssets();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [loadAssets, showFeedback, handleError],
  );

  const handleDeleteMaintenance = useCallback(
    async (assetId, maintenance) => {
      if (!window.confirm('Remover o registro de manutenção selecionado?')) return;
      try {
        await AssetService.deleteMaintenance(assetId, maintenance.id);
        showFeedback('Registro de manutenção excluído.');
        await loadAssets();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [loadAssets, showFeedback, handleError],
  );

  const handleSaveCategory = useCallback(async () => {
    setSavingCategory(true);
    try {
      if (categoryForm.id) {
        await AssetService.updateCategory(categoryForm.id, categoryForm);
        showFeedback('Categoria atualizada com sucesso.');
      } else {
        await AssetService.createCategory(categoryForm);
        showFeedback('Categoria criada com sucesso.');
      }
      setCategoryModalOpen(false);
      await loadCategories();
      await loadAssets();
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setSavingCategory(false);
    }
  }, [categoryForm, loadCategories, loadAssets, showFeedback, handleError]);

  const handleDeleteCategory = useCallback(
    async (category) => {
      if (!category) return;
      if (!window.confirm(`Excluir a categoria "${category.name}"?`)) return;
      try {
        await AssetService.deleteCategory(category.id);
        showFeedback('Categoria excluída com sucesso.');
        await loadCategories();
        await loadAssets();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [loadCategories, loadAssets, showFeedback, handleError],
  );

  const handleSaveLocation = useCallback(async () => {
    setSavingLocation(true);
    try {
      if (locationForm.id) {
        await AssetService.updateLocation(locationForm.id, locationForm);
        showFeedback('Localização atualizada com sucesso.');
      } else {
        await AssetService.createLocation(locationForm);
        showFeedback('Localização criada com sucesso.');
      }
      setLocationModalOpen(false);
      await loadLocations();
      await loadAssets();
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setSavingLocation(false);
    }
  }, [locationForm, loadLocations, loadAssets, showFeedback, handleError]);

  const handleDeleteLocation = useCallback(
    async (location) => {
      if (!location) return;
      if (!window.confirm(`Excluir a localização "${location.name}"?`)) return;
      try {
        await AssetService.deleteLocation(location.id);
        showFeedback('Localização excluída com sucesso.');
        await loadLocations();
        await loadAssets();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [loadLocations, loadAssets, showFeedback, handleError],
  );

  const handleExportCsv = useCallback(() => {
    if (filteredAssets.length === 0) {
      handleError('Não há dados para exportar.');
      return;
    }
    try {
      const rows = filteredAssets.map((asset) => [
        asset.tag || '',
        asset.name || '',
        asset.category?.name || '',
        asset.location?.name || '',
        statusLabels[asset.status] || asset.status,
        formatCurrency(asset.acquisitionCost),
        formatCurrency(asset.currentValue),
        formatDate(asset.acquisitionDate),
        formatDate(asset.warrantyEnd),
        asset.custodian || '',
        formatPercentage(asset.depreciationPercentage),
      ]);

      const header = [
        'Etiqueta',
        'Ativo',
        'Categoria',
        'Localização',
        'Status',
        'Valor de aquisição',
        'Valor contábil estimado',
        'Data de aquisição',
        'Garantia até',
        'Responsável',
        'Depreciação acumulada',
      ];

      const csv = [header, ...rows]
        .map((row) =>
          row
            .map((value) => {
              const text = String(value ?? '').replace(/"/g, '""');
              return `"${text}"`;
            })
            .join(';'),
        )
        .join('\n');

      if (typeof window !== 'undefined' && window.document) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute(
          'download',
          `patrimonio-${new Date().toISOString().slice(0, 10)}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        showFeedback('Relatório exportado com sucesso.');
      }
    } catch (err) {
      handleError('Não foi possível exportar o relatório.');
    }
  }, [filteredAssets, showFeedback, handleError]);

  const handleExportExcel = useCallback(
    (format) => {
      if (filteredAssets.length === 0) {
        handleError('Não há dados para exportar.');
        return;
      }

      try {
        const rows = filteredAssets.map((asset) => [
          asset.tag || '',
          asset.name || '',
          asset.category?.name || '',
          asset.location?.name || '',
          statusLabels[asset.status] || asset.status,
          formatCurrency(asset.acquisitionCost),
          formatCurrency(asset.currentValue),
          formatDate(asset.acquisitionDate),
          formatDate(asset.warrantyEnd),
          asset.custodian || '',
          formatPercentage(asset.depreciationPercentage),
        ]);

        const header = [
          'Etiqueta',
          'Ativo',
          'Categoria',
          'Localização',
          'Status',
          'Valor de aquisição',
          'Valor contábil estimado',
          'Data de aquisição',
          'Garantia até',
          'Responsável',
          'Depreciação acumulada',
        ];

        const worksheet = XLSXUtils.aoa_to_sheet([header, ...rows]);
        const workbook = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(workbook, worksheet, 'Patrimônio');

        const suffix = new Date().toISOString().slice(0, 10);
        const filename = `patrimonio-${suffix}.${format}`;
        const bookType = format === 'xls' ? 'biff8' : 'xlsx';

        writeXLSXFile(workbook, filename, { bookType });
        showFeedback('Relatório exportado com sucesso.');
      } catch (err) {
        console.error('Erro ao exportar planilha de patrimônio:', err);
        handleError('Não foi possível exportar o relatório.');
      }
    },
    [filteredAssets, showFeedback, handleError],
  );

  const handleExportPdf = useCallback(async () => {
    if (filteredAssets.length === 0) {
      handleError('Não há dados para exportar.');
      return;
    }

    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 40;
      let cursorY = 70;

      const logoPath = `${process.env.PUBLIC_URL || ''}/nashlogoazul.png`;
      const logoDataUrl = await loadImageAsDataURL(logoPath);
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, 'PNG', margin, cursorY - 30, 70, 70);
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(15, 23, 42);
      doc.text('Relatório de Patrimônio', margin + 90, cursorY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(71, 85, 105);
      doc.text(
        'Relatório gerado pelo sistema Intelligent BPM Nash Tecnologia. Site: www.nashtec.com.br',
        margin + 90,
        cursorY + 20,
      );

      cursorY += 70;
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, cursorY, pageWidth - margin, cursorY);
      cursorY += 30;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(15, 23, 42);
      doc.text('Visão geral', margin, cursorY);
      cursorY += 22;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(71, 85, 105);
      const summaryLines = [
        `Total de ativos monitorados: ${filteredTotals.assetCount}`,
        `Valor de aquisição acumulado: ${formatCurrency(filteredTotals.totalAcquisition)}`,
        `Valor contábil estimado: ${formatCurrency(filteredTotals.totalCurrent)}`,
        `Depreciação média: ${formatPercentage(filteredTotals.averageDepreciation)}`,
      ];

      summaryLines.forEach((line) => {
        doc.text(line, margin, cursorY);
        cursorY += 18;
      });

      cursorY += 10;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(15, 23, 42);
      doc.text('Indicadores visuais', margin, cursorY);
      cursorY += 16;

      const chartAreaWidth = pageWidth - margin * 2;
      const chartHeight = 220;
      const chartSpacing = 48;
      const chartImages = [];

      if (statusDistributionSummary.labels.length) {
        const statusChart = await createChartImage({
          type: 'doughnut',
          data: {
            labels: statusDistributionSummary.labels,
            datasets: [
              {
                data: statusDistributionSummary.values,
                backgroundColor: chartPalette.slice(0, statusDistributionSummary.labels.length),
                borderWidth: 0,
              },
            ],
          },
          options: {
            plugins: {
              title: {
                text: 'Distribuição por status',
              },
            },
          },
        });
        if (statusChart) {
          chartImages.push({ image: statusChart, title: 'Status' });
        }
      }

      if (categoryDistributionSummary.labels.length) {
        const categoryChart = await createChartImage({
          type: 'bar',
          data: {
            labels: categoryDistributionSummary.labels,
            datasets: [
              {
                label: 'Ativos',
                data: categoryDistributionSummary.values,
                backgroundColor: chartPalette.slice(0, categoryDistributionSummary.labels.length),
                borderRadius: 8,
              },
            ],
          },
          options: {
            scales: {
              x: {
                ticks: {
                  color: '#334155',
                  font: {
                    family: 'Helvetica',
                    size: 12,
                  },
                },
              },
              y: {
                ticks: {
                  color: '#334155',
                  font: {
                    family: 'Helvetica',
                    size: 12,
                  },
                },
                beginAtZero: true,
              },
            },
            plugins: {
              title: {
                text: 'Ativos por categoria',
              },
            },
          },
        });
        if (categoryChart) {
          chartImages.push({ image: categoryChart, title: 'Categorias' });
        }
      }

      chartImages.forEach((chartImage) => {
        if (cursorY + chartHeight > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          cursorY = margin;
        }
        doc.addImage(chartImage.image, 'PNG', margin, cursorY, chartAreaWidth, chartHeight);
        cursorY += chartHeight + chartSpacing;
      });

      const maintenanceHighlights = filteredAssets
        .flatMap((asset) =>
          (asset.maintenance || [])
            .filter((entry) => entry.status !== 'COMPLETED')
            .map((entry) => ({ asset, entry })),
        )
        .sort((a, b) => {
          const dateA = new Date(a.entry.scheduledAt || a.entry.performedAt || 0).getTime();
          const dateB = new Date(b.entry.scheduledAt || b.entry.performedAt || 0).getTime();
          return dateA - dateB;
        })
        .slice(0, 5);

      doc.addPage();
      cursorY = margin;

      if (maintenanceHighlights.length) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(15, 23, 42);
        doc.text('Próximas intervenções de manutenção', margin, cursorY);
        cursorY += 18;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(71, 85, 105);

        maintenanceHighlights.forEach(({ asset, entry }, index) => {
          const label = `${index + 1}. ${asset.tag || asset.name} • ${
            maintenanceTypeLabels[entry.type] || entry.type
          } • ${maintenanceStatusLabels[entry.status] || entry.status}`;
          const date = entry.scheduledAt || entry.performedAt;
          const details = [
            date ? `Data: ${formatDate(date)}` : null,
            entry.responsible ? `Responsável: ${entry.responsible}` : null,
            entry.cost ? `Custo estimado: ${formatCurrency(entry.cost)}` : null,
          ]
            .filter(Boolean)
            .join(' | ');

          const detailText = details ? `   ${details}` : '';
          const lines = doc.splitTextToSize(`${label}${detailText ? `\n${detailText}` : ''}`, chartAreaWidth);
          lines.forEach((line) => {
            if (cursorY > doc.internal.pageSize.getHeight() - margin) {
              doc.addPage();
              cursorY = margin;
            }
            doc.text(line, margin, cursorY);
            cursorY += 14;
          });
          cursorY += 4;
        });
      }

      cursorY += 12;
      if (cursorY > doc.internal.pageSize.getHeight() - margin - 24) {
        doc.addPage();
        cursorY = margin;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(15, 23, 42);
      doc.text('Relação completa de ativos', margin, cursorY);
      cursorY += 18;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(71, 85, 105);

      if (!filteredAssets.length) {
        doc.text('Nenhum ativo encontrado para os filtros selecionados.', margin, cursorY);
        cursorY += 14;
      } else {
        filteredAssets.forEach((asset, index) => {
          const categoryLabel = resolveTextValue(asset.category);
          const locationLabel = resolveTextValue(asset.location);
          const details = [
            categoryLabel ? `Categoria: ${categoryLabel}` : null,
            locationLabel ? `Local: ${locationLabel}` : null,
            asset.status ? `Status: ${statusLabels[asset.status] || asset.status}` : null,
            asset.currentValue ? `Valor atual: ${formatCurrency(asset.currentValue)}` : null,
          ]
            .filter(Boolean)
            .join(' | ');

          const mainName = resolveTextValue(asset.name) || resolveTextValue(asset.tag) || 'Ativo sem nome';
          const resolvedTag = resolveTextValue(asset.tag);
          const tagSuffix = resolvedTag && resolvedTag !== mainName ? ` (${resolvedTag})` : '';
          const header = `${index + 1}. ${mainName}${tagSuffix}`;
          const lines = doc.splitTextToSize(
            details ? `${header}\n   ${details}` : header,
            chartAreaWidth,
          );

          lines.forEach((line) => {
            if (cursorY > doc.internal.pageSize.getHeight() - margin) {
              doc.addPage();
              cursorY = margin;
            }
            doc.text(line, margin, cursorY);
            cursorY += 14;
          });

          cursorY += 4;
        });
      }

      if (cursorY > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        cursorY = margin;
      }

      doc.setDrawColor(226, 232, 240);
      doc.line(margin, doc.internal.pageSize.getHeight() - margin, pageWidth - margin, doc.internal.pageSize.getHeight() - margin);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Emitido em ${new Date().toLocaleString('pt-BR')}`,
        margin,
        doc.internal.pageSize.getHeight() - margin + 18,
      );

      doc.save(`patrimonio-${new Date().toISOString().slice(0, 10)}.pdf`);
      showFeedback('Relatório exportado com sucesso.');
    } catch (err) {
      console.error('Erro ao exportar relatório em PDF:', err);
      handleError('Não foi possível exportar o relatório.');
    }
  }, [
    filteredAssets,
    filteredTotals,
    statusDistributionSummary,
    categoryDistributionSummary,
    showFeedback,
    handleError,
  ]);

  const handleDownloadImportTemplate = useCallback(() => {
    try {
      const statusOptions = STATUS_OPTIONS.map((option) => option.label);
      const categoryOptions = categories.map((category) => category.name).filter(Boolean);
      const locationOptions = locations.map((location) => location.name).filter(Boolean);

      const templateRows = [IMPORT_COLUMNS.reduce((acc, column) => ({ ...acc, [getImportColumnLabel(column)]: '' }), {})];
      const worksheet = XLSXUtils.json_to_sheet(templateRows, {
        header: IMPORT_COLUMNS.map((column) => getImportColumnLabel(column)),
        skipHeader: false,
      });
      worksheet['!cols'] = IMPORT_COLUMNS.map((column) => ({ wch: Math.max(column.label.length + 3, 20) }));

      const instructionsRows = IMPORT_COLUMNS.map((column) => ({
        Coluna: getImportColumnLabel(column),
        Obrigatorio: column.required ? 'Sim' : 'Não',
        Orientacao: column.instructions,
      }));
      const instructionsSheet = XLSXUtils.json_to_sheet(instructionsRows);
      instructionsSheet['!cols'] = [{ wch: 28 }, { wch: 12 }, { wch: 60 }];

      const optionsSheet = XLSXUtils.json_to_sheet([
        { Tipo: 'Status', Opcoes: statusOptions.join(' | ') || 'Sem opções cadastradas' },
        { Tipo: 'Categorias cadastradas', Opcoes: categoryOptions.join(' | ') || 'Sem categorias cadastradas' },
        { Tipo: 'Locais cadastrados', Opcoes: locationOptions.join(' | ') || 'Sem locais cadastrados' },
      ]);
      optionsSheet['!cols'] = [{ wch: 28 }, { wch: 120 }];

      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Patrimonio');
      XLSXUtils.book_append_sheet(workbook, instructionsSheet, 'Instrucoes');
      XLSXUtils.book_append_sheet(workbook, optionsSheet, 'Opcoes');
      writeXLSXFile(workbook, 'template-importacao-patrimonio.xlsx', { bookType: 'xlsx' });
      showFeedback('Template de importação gerado com sucesso.');
    } catch (err) {
      handleError('Não foi possível gerar o template de importação.');
    }
  }, [categories, locations, handleError, showFeedback]);

  const handleImportFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      try {
        const buffer = await file.arrayBuffer();
        const workbook = readXLSX(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) {
          throw new Error('Planilha inválida. Não foi encontrada nenhuma aba de dados.');
        }

        const rows = XLSXUtils.sheet_to_json(worksheet, { defval: '' });
        if (!rows.length) {
          setImportPreviewRows([]);
          handleError('A planilha está vazia.');
          return;
        }

        const categoryByName = new Map(categories.map((category) => [normalizeText(category.name), category]));
        const locationByName = new Map(locations.map((location) => [normalizeText(location.name), location]));
        const statusByText = STATUS_OPTIONS.reduce((acc, option) => {
          acc[normalizeText(option.value)] = option.value;
          acc[normalizeText(option.label)] = option.value;
          return acc;
        }, {});

        const preview = rows.map((row, index) => {
          const normalizedRow = {};
          Object.entries(row).forEach(([header, value]) => {
            normalizedRow[normalizeHeader(header)] = value;
          });

          const payload = {};
          IMPORT_COLUMNS.forEach((column) => {
            const canonicalHeader = normalizeHeader(column.label);
            const starredHeader = normalizeHeader(getImportColumnLabel(column));
            payload[column.key] = resolveTextValue(
              normalizedRow[starredHeader] ?? normalizedRow[canonicalHeader],
            );
          });

          const errors = [];
          IMPORT_REQUIRED_FIELDS.forEach((field) => {
            if (!payload[field]) {
              const column = IMPORT_COLUMNS.find((item) => item.key === field);
              errors.push(`"${column?.label || field}" é obrigatório.`);
            }
          });

          const category = categoryByName.get(normalizeText(payload.category));
          const location = locationByName.get(normalizeText(payload.location));
          const status = statusByText[normalizeText(payload.status)] || '';

          if (payload.category && !category) errors.push(`Categoria "${payload.category}" não cadastrada.`);
          if (payload.location && !location) errors.push(`Localização "${payload.location}" não cadastrada.`);
          if (payload.status && !status) errors.push(`Status "${payload.status}" inválido.`);

          return {
            rowNumber: index + 2,
            payload: {
              ...payload,
              status: status || payload.status,
              categoryId: category?.id || '',
              locationId: location?.id || '',
              acquisitionCost: parseImportNumber(payload.acquisitionCost),
              depreciationRate: parseImportNumber(payload.depreciationRate),
              usefulLifeMonths: parseImportNumber(payload.usefulLifeMonths),
            },
            errors,
          };
        });

        setImportPreviewRows(preview);
        const invalidCount = preview.filter((item) => item.errors.length > 0).length;
        if (invalidCount > 0) {
          showFeedback(`Prévia carregada com ${preview.length} linha(s). ${invalidCount} linha(s) com pendências.`, 'warning');
        } else {
          showFeedback(`Prévia carregada com ${preview.length} linha(s) pronta(s) para confirmação.`);
        }
      } catch (err) {
        handleError(err?.message || 'Não foi possível ler a planilha.');
        setImportPreviewRows([]);
      }
    },
    [categories, locations, handleError, showFeedback],
  );

  const handleConfirmImport = useCallback(async () => {
    if (!importPreviewRows.length) {
      handleError('Carregue uma planilha antes de confirmar a importação.');
      return;
    }

    const invalidRows = importPreviewRows.filter((row) => row.errors.length > 0);
    if (invalidRows.length > 0) {
      const missingRequired = new Set();
      invalidRows.forEach((row) => {
        row.errors.forEach((message) => {
          if (message.includes('obrigatório')) {
            missingRequired.add(message.replace(' é obrigatório.', ''));
          }
        });
      });
      handleError(
        `Existem ${invalidRows.length} linha(s) inválida(s). Campos obrigatórios faltantes: ${
          missingRequired.size ? Array.from(missingRequired).join(', ') : 'revise a planilha'
        }.`,
      );
      return;
    }

    setImportingAssets(true);
    try {
      for (const row of importPreviewRows) {
        await AssetService.createAsset({
          name: row.payload.name,
          tag: row.payload.tag,
          serialNumber: row.payload.serialNumber,
          status: row.payload.status,
          categoryId: row.payload.categoryId,
          locationId: row.payload.locationId,
          acquisitionDate: row.payload.acquisitionDate || '',
          acquisitionCost: row.payload.acquisitionCost ?? '',
          depreciationRate: row.payload.depreciationRate ?? '',
          usefulLifeMonths: row.payload.usefulLifeMonths ?? '',
          warrantyEnd: row.payload.warrantyEnd || '',
          custodian: row.payload.custodian,
          supplier: row.payload.supplier,
          notes: row.payload.notes,
        });
      }
      await loadAssets();
      setImportPreviewRows([]);
      showFeedback(`${importPreviewRows.length} linha(s) inserida(s) com sucesso.`);
    } catch (err) {
      handleError(resolveErrorMessage(err));
    } finally {
      setImportingAssets(false);
    }
  }, [importPreviewRows, loadAssets, handleError, showFeedback]);

  const renderOverviewTab = () => {
    const statusTotal = statusDistributionSummary.values.reduce(
      (acc, value) => acc + value,
      0,
    );
    const categoryTotal = categoryDistributionSummary.values.reduce(
      (acc, value) => acc + value,
      0,
    );

    return (
      <div className="space-y-6">
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs text-slate-500">Ativos cadastrados</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-3xl font-semibold text-slate-800">{assets.length}</p>
              <span className="rounded-full bg-slate-100 p-3 text-slate-500">
                <FaClipboardList />
              </span>
            </div>
          </article>
          <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs text-slate-500">Valor de aquisição</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-2xl font-semibold text-slate-800">
                {formatCurrency(totalAcquisitionValue)}
              </p>
              <span className="rounded-full bg-emerald-100 p-3 text-emerald-600">
                <FaChartLine />
              </span>
            </div>
          </article>
          <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs text-slate-500">Valor contábil estimado</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-2xl font-semibold text-slate-800">
                {formatCurrency(totalCurrentValue)}
              </p>
              <span className="rounded-full bg-sky-100 p-3 text-sky-600">
                <FaBoxOpen />
              </span>
            </div>
          </article>
          <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs text-slate-500">Alertas</p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-2xl font-semibold text-slate-800">
                {pendingMaintenanceCount + warrantySummary.expiring.length}
              </p>
              <span className="rounded-full bg-amber-100 p-3 text-amber-600">
                <FaExclamationTriangle />
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {pendingMaintenanceCount} manutenções · {warrantySummary.expiring.length} garantias
            </p>
          </article>
        </section>

        <div className="grid gap-6 xl:grid-cols-[2fr,1fr]">
          <section className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Distribuição por status</h2>
                <span className="text-xs text-slate-500">{statusTotal} ativos</span>
              </div>
              {statusTotal === 0 ? (
                <p className="mt-4 text-sm text-slate-500">Sem dados para os filtros atuais.</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {statusDistributionSummary.labels.map((label, index) => {
                    const value = statusDistributionSummary.values[index];
                    const percentage = statusTotal ? Math.round((value / statusTotal) * 100) : 0;
                    const color = chartPalette[index % chartPalette.length];
                    return (
                      <li
                        key={label}
                        className="rounded-xl border border-slate-100 bg-slate-50 p-3 shadow-sm"
                      >
                        <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                          <span className="flex items-center gap-2">
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                            {label}
                          </span>
                          <span>{value}</span>
                        </div>
                        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${percentage}%`, backgroundColor: color }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Categorias em uso</h2>
                <span className="text-xs text-slate-500">{categoryTotal} ativos</span>
              </div>
              {categoryTotal === 0 ? (
                <p className="mt-4 text-sm text-slate-500">Cadastre ativos para visualizar.</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {categoryDistributionSummary.labels.slice(0, 6).map((label, index) => {
                    const value = categoryDistributionSummary.values[index];
                    const percentage = categoryTotal
                      ? Math.round((value / categoryTotal) * 100)
                      : 0;
                    const color = chartPalette[index % chartPalette.length];
                    return (
                      <li
                        key={label}
                        className="rounded-xl border border-slate-100 bg-slate-50 p-3 shadow-sm"
                      >
                        <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                          <span className="flex items-center gap-2">
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                            {label}
                          </span>
                          <span>{value}</span>
                        </div>
                        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${percentage}%`, backgroundColor: color }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Saúde dos ativos</h2>
                <span className="text-xs text-slate-500">{highlyDepreciatedAssets} críticos</span>
              </div>
              {topDepreciatedAssets.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">Todos os ativos estão dentro da vida útil.</p>
              ) : (
                <ul className="mt-4 space-y-3 text-sm text-slate-600">
                  {topDepreciatedAssets.slice(0, 3).map((asset) => (
                    <li
                      key={asset.id}
                      className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 shadow-sm"
                    >
                      <span className="truncate font-medium text-slate-700">{asset.name}</span>
                      <span className="text-sm font-semibold text-rose-600">
                        {formatPercentage(asset.depreciationPercentage)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <dl className="mt-4 grid grid-cols-2 gap-3 text-xs text-slate-500">
                <div className="rounded-lg bg-slate-50 p-3">
                  <dt className="font-medium text-slate-600">Locais</dt>
                  <dd className="text-sm font-semibold text-slate-800">{locations.length}</dd>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <dt className="font-medium text-slate-600">Categorias</dt>
                  <dd className="text-sm font-semibold text-slate-800">{categories.length}</dd>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <dt className="font-medium text-slate-600">Manutenções abertas</dt>
                  <dd className="text-sm font-semibold text-slate-800">{pendingMaintenanceCount}</dd>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <dt className="font-medium text-slate-600">Garantias ativas</dt>
                  <dd className="text-sm font-semibold text-slate-800">
                    {assetsWithMetrics.filter((asset) => asset.warrantyEnd).length}
                  </dd>
                </div>
              </dl>
            </div>
          </section>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Próximas manutenções</h2>
                <button
                  type="button"
                  onClick={() => setActiveTab('maintenance')}
                  className="text-xs font-semibold text-sky-600 transition hover:text-sky-700"
                >
                  ver todas
                </button>
              </div>
              <ul className="mt-4 space-y-3">
                {upcomingMaintenance.length === 0 ? (
                  <li className="text-sm text-slate-500">Nada agendado.</li>
                ) : (
                  upcomingMaintenance.slice(0, 4).map(({ asset, entry }) => {
                    const days = daysUntil(entry.scheduledAt || entry.performedAt);
                    const badge =
                      days === null
                        ? 'bg-slate-100 text-slate-600'
                        : days < 0
                        ? 'bg-rose-100 text-rose-700'
                        : days <= 7
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-100 text-emerald-700';
                    return (
                      <li key={entry.id} className="rounded-xl border border-slate-100 p-3 shadow-sm">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{asset.name}</p>
                            <p className="text-xs text-slate-500">
                              {MAINTENANCE_TYPE_OPTIONS.find((option) => option.value === entry.type)?.label || '—'}
                              {' · '}
                              {entry.responsible || 'Sem responsável'}
                            </p>
                          </div>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badge}`}>
                            {days === null
                              ? 'sem data'
                              : days < 0
                              ? `${Math.abs(days)}d atraso`
                              : `${days}d`}
                          </span>
                        </div>
                        <p className="mt-2 flex items-center gap-1 text-xs text-slate-400">
                          <FaCalendarCheck className="text-slate-300" />
                          {entry.scheduledAt ? formatDate(entry.scheduledAt) : 'Definir data'}
                        </p>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Garantias</h2>
                <button
                  type="button"
                  onClick={() => setActiveTab('maintenance')}
                  className="text-xs font-semibold text-sky-600 transition hover:text-sky-700"
                >
                  ver detalhes
                </button>
              </div>
              <ul className="mt-4 space-y-3">
                {warrantySummary.expiring.length === 0 ? (
                  <li className="text-sm text-slate-500">Sem vencimentos nos próximos 45 dias.</li>
                ) : (
                  warrantySummary.expiring.slice(0, 4).map((asset) => (
                    <li
                      key={asset.id}
                      className="rounded-xl border border-amber-100 bg-amber-50 p-3 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-amber-800">{asset.name}</p>
                          <p className="text-xs text-amber-700">
                            {asset.custodian || 'Responsável não informado'}
                          </p>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-700 shadow-sm">
                          <FaCalendarTimes />
                          {formatDate(asset.warrantyEnd)}
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-amber-700">Expira em {asset.days} dias</p>
                    </li>
                  ))
                )}
              </ul>
              {warrantySummary.expired.length > 0 && (
                <p className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {warrantySummary.expired.length} garantias vencidas
                </p>
              )}
            </div>
          </aside>
        </div>
      </div>
    );
  };

  const renderAssetsTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <button
          type="button"
          onClick={handleExportCsv}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
        >
          <FaDownload />
          Exportar CSV
        </button>
        <button
          type="button"
          onClick={() => handleExportExcel('xls')}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
        >
          <FaDownload />
          Exportar XLS
        </button>
        <button
          type="button"
          onClick={() => handleExportExcel('xlsx')}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
        >
          <FaDownload />
          Exportar XLSX
        </button>
        <button
          type="button"
          onClick={handleExportPdf}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
        >
          <FaDownload />
          Exportar PDF
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Buscar</label>
            <input
              type="text"
              value={filters.search}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, search: event.target.value }))
              }
              placeholder="Nome, código ou responsável"
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Status</label>
            <select
              value={filters.status}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, status: event.target.value }))
              }
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            >
              <option value="all">Todos</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Categoria</label>
            <select
              value={filters.categoryId}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, categoryId: event.target.value }))
              }
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            >
              <option value="all">Todas</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500">Localização</label>
            <select
              value={filters.locationId}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, locationId: event.target.value }))
              }
              className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
            >
              <option value="all">Todas</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {canManage && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => openCategoryModal(null)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              <FaCogs />
              Nova categoria
            </button>
            <button
              type="button"
              onClick={() => openLocationModal(null)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              <FaMapMarkedAlt />
              Novo local
            </button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-[920px] divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Ativo</th>
                <th className="px-4 py-3 text-left">Categoria</th>
                <th className="px-4 py-3 text-left">Local</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Aquisição</th>
                <th className="px-4 py-3 text-right">Valor atual</th>
                <th className="px-4 py-3 text-left">Responsável</th>
                <th className="px-4 py-3 text-right">Depreciação</th>
                {canManage && <th className="px-4 py-3 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr>
                  <td
                    colSpan={canManage ? 9 : 8}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    Carregando ativos...
                  </td>
                </tr>
              ) : filteredAssets.length === 0 ? (
                <tr>
                  <td
                    colSpan={canManage ? 9 : 8}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    Nenhum ativo encontrado.
                  </td>
                </tr>
              ) : (
                filteredAssets.map((asset) => (
                  <tr key={asset.id} className="transition hover:bg-slate-50">
                    <td className="px-4 py-4">
                      <div className="font-semibold text-slate-800">{asset.name}</div>
                      <div className="text-xs text-slate-400">{asset.tag || asset.serialNumber || '—'}</div>
                      {Array.isArray(asset.attachments) && asset.attachments.length > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-sky-600">
                          <FaPaperclip />
                          {asset.attachments.length} anexo(s)
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">{asset.category?.name || '—'}</td>
                    <td className="px-4 py-4 text-sm text-slate-600">{asset.location?.name || '—'}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${
                          statusBadgeStyles[asset.status] || 'bg-slate-100 text-slate-600 border-slate-200'
                        }`}
                      >
                        {statusLabels[asset.status] || asset.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-slate-600">
                      {formatCurrency(asset.acquisitionCost)}
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-slate-600">
                      {formatCurrency(asset.currentValue)}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">{asset.custodian || '—'}</td>
                    <td className="px-4 py-4 text-right text-sm text-slate-600">
                      {formatPercentage(asset.depreciationPercentage)}
                    </td>
                    {canManage && (
                      <td className="px-4 py-4 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openMaintenanceModal(asset)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                          >
                            <FaTools />
                            Manutenção
                          </button>
                          <button
                            type="button"
                            onClick={() => openAssetModal(asset)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                          >
                            <FaEdit />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAsset(asset)}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                          >
                            <FaTrash />
                            Excluir
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderImportTab = () => (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-800">Importação via planilha (Excel)</h2>
        <p className="mt-1 text-sm text-slate-500">
          Siga os passos abaixo para importar ativos de patrimônio com validação de campos obrigatórios.
        </p>
        <ol className="mt-4 space-y-2 text-sm text-slate-600">
          <li><strong>1.</strong> Baixe o template padrão e preencha os dados (colunas com <strong>*</strong> são obrigatórias).</li>
          <li><strong>2.</strong> Use as opções da aba <strong>Opcoes</strong> para Status, Categorias e Locais.</li>
          <li><strong>3.</strong> Envie o arquivo para carregar a prévia.</li>
          <li><strong>4.</strong> Confira os dados na tabela abaixo e confirme a importação.</li>
        </ol>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleDownloadImportTemplate}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            <FaDownload />
            Baixar template
          </button>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700">
            <FaFileImport />
            Selecionar planilha
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportFileChange} />
          </label>
          <button
            type="button"
            onClick={handleConfirmImport}
            disabled={!importPreviewRows.length || importingAssets}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {importingAssets ? 'Importando…' : 'Confirmar importação'}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Prévia antes de inserir</h3>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
            {importPreviewRows.length} linha(s)
          </span>
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-[980px] divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Linha</th>
                <th className="px-4 py-3 text-left">Ativo</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Categoria</th>
                <th className="px-4 py-3 text-left">Localização</th>
                <th className="px-4 py-3 text-left">Pendências</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {importPreviewRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                    Nenhuma planilha carregada para prévia.
                  </td>
                </tr>
              ) : (
                importPreviewRows.map((row) => (
                  <tr key={row.rowNumber} className={row.errors.length ? 'bg-rose-50/60' : ''}>
                    <td className="px-4 py-3 text-slate-600">{row.rowNumber}</td>
                    <td className="px-4 py-3 text-slate-700">{row.payload.name || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{row.payload.status || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{row.payload.category || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{row.payload.location || '—'}</td>
                    <td className="px-4 py-3 text-xs text-rose-700">
                      {row.errors.length ? row.errors.join(' ') : 'Sem pendências'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderMaintenanceTab = () => (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800">Próximas manutenções</h2>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
              {upcomingMaintenance.length}
            </span>
          </div>
          <ul className="mt-4 space-y-3">
            {upcomingMaintenance.length === 0 ? (
              <li className="text-sm text-slate-500">Nenhuma manutenção programada.</li>
            ) : (
              upcomingMaintenance.slice(0, 10).map(({ asset, entry }) => {
                const days = daysUntil(entry.scheduledAt || entry.performedAt);
                const badge =
                  days === null
                    ? 'bg-slate-100 text-slate-600'
                    : days < 0
                    ? 'bg-rose-100 text-rose-700'
                    : days <= 7
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-emerald-100 text-emerald-700';
                return (
                  <li key={entry.id} className="rounded-xl border border-slate-100 p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{asset.name}</p>
                        <p className="text-xs text-slate-500">
                          {MAINTENANCE_TYPE_OPTIONS.find((option) => option.value === entry.type)?.label || '—'}
                          {' · '}
                          {entry.responsible || 'Sem responsável'}
                        </p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badge}`}>
                        {days === null
                          ? 'Sem data'
                          : days < 0
                          ? `${Math.abs(days)} dias atraso`
                          : `${days} dias`}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <FaCalendarCheck className="text-slate-400" />
                        {entry.scheduledAt ? formatDate(entry.scheduledAt) : 'Definir data'}
                      </span>
                      {canManage && entry.status !== 'COMPLETED' && (
                        <button
                          type="button"
                          onClick={() => handleCompleteMaintenance(asset.id, entry)}
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-600 transition hover:border-emerald-300 hover:text-emerald-700"
                        >
                          <FaCheckCircle />
                          Concluir
                        </button>
                      )}
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800">Garantias</h2>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
              {warrantySummary.expiring.length}
            </span>
          </div>
          <ul className="mt-4 space-y-3">
            {warrantySummary.expiring.length === 0 ? (
              <li className="text-sm text-slate-500">Sem vencimentos em 45 dias.</li>
            ) : (
              warrantySummary.expiring.map((asset) => (
                <li key={asset.id} className="rounded-xl border border-amber-100 bg-amber-50 p-3 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-amber-800">{asset.name}</p>
                      <p className="text-xs text-amber-700">
                        {asset.custodian || 'Responsável não informado'}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-700 shadow-sm">
                      <FaCalendarTimes />
                      {formatDate(asset.warrantyEnd)}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-amber-700">Expira em {asset.days} dias</p>
                </li>
              ))
            )}
          </ul>
          {warrantySummary.expired.length > 0 && (
            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
              <strong className="font-semibold">Garantias vencidas:</strong>{' '}
              {warrantySummary.expired
                .slice(0, 4)
                .map((asset) => asset.name)
                .join(', ')}
              {warrantySummary.expired.length > 4 && '…'}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Histórico de manutenções</h2>
            <p className="text-xs text-slate-500">Últimos registros lançados</p>
          </div>
          {canManage && (
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
              {maintenanceHistory.length} registros
            </span>
          )}
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-[720px] divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Ativo</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Responsável</th>
                <th className="px-4 py-3 text-right">Data</th>
                <th className="px-4 py-3 text-right">Custo</th>
                {canManage && <th className="px-4 py-3 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {maintenanceHistory.length === 0 ? (
                <tr>
                  <td
                    colSpan={canManage ? 7 : 6}
                    className="px-4 py-5 text-center text-sm text-slate-500"
                  >
                    Ainda não há histórico suficiente para exibição.
                  </td>
                </tr>
              ) : (
                maintenanceHistory.map(({ asset, entry }) => (
                  <tr key={entry.id} className="transition hover:bg-slate-50">
                    <td className="px-4 py-4">
                      <div className="font-semibold text-slate-800">{asset.name}</div>
                      <div className="text-xs text-slate-400">{asset.tag || asset.serialNumber || '—'}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">
                      {MAINTENANCE_TYPE_OPTIONS.find((option) => option.value === entry.type)?.label || '—'}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">
                      {MAINTENANCE_STATUS_OPTIONS.find((option) => option.value === entry.status)?.label || '—'}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">{entry.responsible || '—'}</td>
                    <td className="px-4 py-4 text-right text-sm text-slate-600">
                      {entry.performedAt ? formatDate(entry.performedAt) : formatDate(entry.scheduledAt)}
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-slate-600">
                      {entry.cost ? formatCurrency(entry.cost) : '—'}
                    </td>
                    {canManage && (
                      <td className="px-4 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {entry.status !== 'COMPLETED' && (
                            <button
                              type="button"
                              onClick={() => handleCompleteMaintenance(asset.id, entry)}
                              className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-600 transition hover:border-emerald-300 hover:text-emerald-700"
                            >
                              <FaCheckCircle />
                              Concluir
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setMaintenanceForm({
                                id: entry.id,
                                assetId: asset.id,
                                type: entry.type,
                                status: entry.status,
                                periodicityValue: entry.periodicityValue
                                  ? String(entry.periodicityValue)
                                  : '',
                                periodicityUnit: entry.periodicityUnit || 'DAYS',
                                scheduledAt: toInputDateTime(entry.scheduledAt),
                                performedAt: toInputDateTime(entry.performedAt),
                                responsible: entry.responsible || '',
                                cost: entry.cost ? String(entry.cost) : '',
                                notes: entry.notes || '',
                              });
                              setMaintenanceModalOpen(true);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                          >
                            <FaEdit />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteMaintenance(asset.id, entry)}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                          >
                            <FaTrash />
                            Excluir
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderDirectoriesTab = () => (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Categorias</h2>
          {canManage && (
            <button
              type="button"
              onClick={() => openCategoryModal(null)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              <FaPlus />
              Nova categoria
            </button>
          )}
        </div>
        <ul className="mt-4 space-y-3 text-sm text-slate-600">
          {categories.length === 0 ? (
            <li className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-4 text-sm text-slate-500">
              Nenhuma categoria cadastrada.
            </li>
          ) : (
            categories.map((category) => {
              const count = assets.filter((asset) => asset.category?.id === category.id).length;
              return (
                <li
                  key={category.id}
                  className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="flex items-center gap-2 font-semibold text-slate-800">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: category.color || '#0ea5e9' }}
                        />
                        {category.name}
                      </p>
                      {category.description && (
                        <p className="mt-1 text-xs text-slate-500">{category.description}</p>
                      )}
                    </div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                      {count} ativo(s)
                    </span>
                  </div>
                  {canManage && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openCategoryModal(category)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                      >
                        <FaEdit />
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCategory(category)}
                        className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                      >
                        <FaTrash />
                        Excluir
                      </button>
                    </div>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Locais</h2>
          {canManage && (
            <button
              type="button"
              onClick={() => openLocationModal(null)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              <FaPlus />
              Novo local
            </button>
          )}
        </div>
        <ul className="mt-4 space-y-3 text-sm text-slate-600">
          {locations.length === 0 ? (
            <li className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-4 text-sm text-slate-500">
              Nenhum local cadastrado.
            </li>
          ) : (
            locations.map((location) => {
              const count = assets.filter((asset) => asset.location?.id === location.id).length;
              return (
                <li
                  key={location.id}
                  className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-800">{location.name}</p>
                      {location.description && (
                        <p className="mt-1 text-xs text-slate-500">{location.description}</p>
                      )}
                      {(location.responsible || location.contact) && (
                        <p className="mt-2 text-xs text-slate-500">
                          {location.responsible && <span>Responsável: {location.responsible}</span>}
                          {location.responsible && location.contact && ' · '}
                          {location.contact && <span>Contato: {location.contact}</span>}
                        </p>
                      )}
                    </div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                      {count} ativo(s)
                    </span>
                  </div>
                  {canManage && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openLocationModal(location)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                      >
                        <FaEdit />
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteLocation(location)}
                        className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                      >
                        <FaTrash />
                        Excluir
                      </button>
                    </div>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );

  if (!canView) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <FaUserShield className="mx-auto mb-4 h-12 w-12 text-slate-400" />
          <h2 className="text-xl font-semibold text-slate-800">
            Você não possui acesso ao módulo de patrimônio.
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Entre em contato com o administrador para solicitar as permissões necessárias.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Controle de Patrimônio</h1>
          <p className="text-sm text-slate-500">Visão clara dos ativos e das manutenções em um só lugar.</p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => openAssetModal(null)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
          >
            <FaPlus />
            Novo ativo
          </button>
        )}
      </header>

      {(feedback || error) && (
        <div className="space-y-3">
          {feedback && (
            <div
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-sm ${
                feedbackType === 'success'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-amber-200 bg-amber-50 text-amber-800'
              }`}
            >
              {feedbackType === 'success' ? (
                <FaCheckCircle className="mt-0.5" />
              ) : (
                <FaExclamationTriangle className="mt-0.5" />
              )}
              <span>{feedback}</span>
            </div>
          )}
          {error && (
            <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 shadow-sm">
              <FaExclamationTriangle className="mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="inline-flex min-w-full gap-2 rounded-2xl border border-slate-200 bg-white p-1">
          {TAB_ITEMS.filter((tab) => canManage || tab.id !== 'import').map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                  isActive ? 'bg-slate-900 text-white shadow' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <Icon />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'assets' && renderAssetsTab()}
        {canManage && activeTab === 'import' && renderImportTab()}
        {activeTab === 'maintenance' && renderMaintenanceTab()}
        {activeTab === 'directories' && renderDirectoriesTab()}
      </div>
      <Modal open={assetModalOpen} onClose={() => setAssetModalOpen(false)} size="max-w-4xl">
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-800">
            {assetForm.id ? 'Editar ativo' : 'Cadastrar ativo'}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Nome do ativo
              </label>
              <input
                type="text"
                value={assetForm.name}
                onChange={(event) => setAssetForm((prev) => ({ ...prev, name: event.target.value }))}
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Status operacional
              </label>
              <select
                value={assetForm.status}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, status: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Categoria
              </label>
              <select
                value={assetForm.categoryId}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, categoryId: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              >
                <option value="">Selecione</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Localização
              </label>
              <select
                value={assetForm.locationId}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, locationId: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              >
                <option value="">Selecione</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Data de aquisição
              </label>
              <input
                type="date"
                value={assetForm.acquisitionDate}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, acquisitionDate: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Valor de aquisição (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={assetForm.acquisitionCost}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, acquisitionCost: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Taxa de depreciação anual (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={assetForm.depreciationRate}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, depreciationRate: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Vida útil (meses)
              </label>
              <input
                type="number"
                value={assetForm.usefulLifeMonths || ''}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, usefulLifeMonths: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Garantia até
              </label>
              <input
                type="date"
                value={assetForm.warrantyEnd}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, warrantyEnd: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Número de série / patrimônio
              </label>
              <input
                type="text"
                value={assetForm.serialNumber}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, serialNumber: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Etiqueta interna
              </label>
              <input
                type="text"
                value={assetForm.tag}
                onChange={(event) => setAssetForm((prev) => ({ ...prev, tag: event.target.value }))}
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Responsável pelo ativo
              </label>
              <input
                type="text"
                value={assetForm.custodian}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, custodian: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Fornecedor / origem
              </label>
              <input
                type="text"
                value={assetForm.supplier}
                onChange={(event) =>
                  setAssetForm((prev) => ({ ...prev, supplier: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Observações e histórico relevante
              </label>
              <textarea
                value={assetForm.notes}
                onChange={(event) => setAssetForm((prev) => ({ ...prev, notes: event.target.value }))}
                rows={4}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Anexos do ativo
              </label>
              <p className="mt-1 text-xs text-slate-500">
                Adicione fotos, atas, termos e demais documentos. Não há limite de quantidade por ativo.
              </p>
              <input
                type="file"
                multiple
                onChange={(event) => {
                  handleAddAssetAttachments(event.target.files);
                  event.target.value = '';
                }}
                className="mt-2 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-sky-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-sky-700 hover:file:bg-sky-100"
              />
              <ul className="mt-3 space-y-2">
                {(assetForm.attachments || []).length === 0 ? (
                  <li className="rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-500">
                    Nenhum anexo incluído para este ativo.
                  </li>
                ) : (
                  (assetForm.attachments || []).map((attachment) => (
                    <li
                      key={attachment.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-700">{attachment.name}</p>
                        <p className="text-xs text-slate-500">
                          {formatFileSize(attachment.size)} • {formatDate(attachment.uploadedAt)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenAssetAttachment(attachment)}
                          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
                        >
                          Abrir
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveAssetAttachment(attachment.id)}
                          className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-600 transition hover:border-rose-300 hover:text-rose-700"
                        >
                          Remover
                        </button>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setAssetModalOpen(false)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveAsset}
              disabled={savingAsset}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <FaCheckCircle />
              {savingAsset ? 'Salvando...' : 'Salvar ativo'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={maintenanceModalOpen}
        onClose={() => setMaintenanceModalOpen(false)}
        size="max-w-3xl"
      >
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-800">
            {maintenanceForm.id ? 'Editar manutenção' : 'Registrar manutenção'}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Tipo de manutenção
              </label>
              <select
                value={maintenanceForm.type}
                onChange={(event) =>
                  setMaintenanceForm((prev) => ({ ...prev, type: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              >
                {MAINTENANCE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Status
              </label>
              <select
                value={maintenanceForm.status}
                onChange={(event) =>
                  setMaintenanceForm((prev) => ({ ...prev, status: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              >
                {MAINTENANCE_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Agendamento
              </label>
              <input
                type="datetime-local"
                value={maintenanceForm.scheduledAt}
                onChange={(event) =>
                  setMaintenanceForm((prev) => ({ ...prev, scheduledAt: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Periodicidade (opcional)
              </label>
              <div className="mt-1 grid gap-3 sm:grid-cols-2">
                <input
                  type="number"
                  min="1"
                  placeholder="Número"
                  value={maintenanceForm.periodicityValue}
                  onChange={(event) =>
                    setMaintenanceForm((prev) => ({
                      ...prev,
                      periodicityValue: event.target.value,
                    }))
                  }
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
                <select
                  value={maintenanceForm.periodicityUnit}
                  onChange={(event) =>
                    setMaintenanceForm((prev) => ({
                      ...prev,
                      periodicityUnit: event.target.value,
                    }))
                  }
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
                >
                  {MAINTENANCE_PERIODICITY_UNIT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Conclusão
              </label>
              <input
                type="datetime-local"
                value={maintenanceForm.performedAt}
                onChange={(event) =>
                  setMaintenanceForm((prev) => ({ ...prev, performedAt: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Responsável
              </label>
              <input
                type="text"
                value={maintenanceForm.responsible}
                onChange={(event) =>
                  setMaintenanceForm((prev) => ({ ...prev, responsible: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Custo (R$)
              </label>
              <input
                type="number"
                step="0.01"
                value={maintenanceForm.cost}
                onChange={(event) =>
                  setMaintenanceForm((prev) => ({ ...prev, cost: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Observações
              </label>
              <textarea
                rows={4}
                value={maintenanceForm.notes}
                onChange={(event) =>
                  setMaintenanceForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setMaintenanceModalOpen(false)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveMaintenance}
              disabled={savingMaintenance}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <FaCheckCircle />
              {savingMaintenance ? 'Salvando...' : 'Salvar manutenção'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        size="max-w-xl"
      >
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-800">
            {categoryForm.id ? 'Editar categoria' : 'Nova categoria'}
          </h2>
          <div className="space-y-4">
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Nome
              </label>
              <input
                type="text"
                value={categoryForm.name}
                onChange={(event) =>
                  setCategoryForm((prev) => ({ ...prev, name: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Descrição
              </label>
              <textarea
                rows={3}
                value={categoryForm.description}
                onChange={(event) =>
                  setCategoryForm((prev) => ({ ...prev, description: event.target.value }))
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Cor identificadora
              </label>
              <input
                type="color"
                value={categoryForm.color}
                onChange={(event) =>
                  setCategoryForm((prev) => ({ ...prev, color: event.target.value }))
                }
                className="mt-1 h-10 w-20 cursor-pointer rounded-lg border border-slate-200"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setCategoryModalOpen(false)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveCategory}
              disabled={savingCategory}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <FaCheckCircle />
              {savingCategory ? 'Salvando...' : 'Salvar categoria'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        size="max-w-xl"
      >
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-800">
            {locationForm.id ? 'Editar local' : 'Novo local'}
          </h2>
          <div className="space-y-4">
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Nome
              </label>
              <input
                type="text"
                value={locationForm.name}
                onChange={(event) =>
                  setLocationForm((prev) => ({ ...prev, name: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Descrição
              </label>
              <textarea
                rows={3}
                value={locationForm.description}
                onChange={(event) =>
                  setLocationForm((prev) => ({ ...prev, description: event.target.value }))
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Responsável
              </label>
              <input
                type="text"
                value={locationForm.responsible}
                onChange={(event) =>
                  setLocationForm((prev) => ({ ...prev, responsible: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Contato
              </label>
              <input
                type="text"
                value={locationForm.contact}
                onChange={(event) =>
                  setLocationForm((prev) => ({ ...prev, contact: event.target.value }))
                }
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setLocationModalOpen(false)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveLocation}
              disabled={savingLocation}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <FaCheckCircle />
              {savingLocation ? 'Salvando...' : 'Salvar local'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
